import { AHText, AHTextFonts, Box, DialogButton, formatName } from "arkh";
import { FormattedMessage } from "react-intl";
import { View } from "react-native";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";
import { ToolbarButtonAssignees_employees$key } from "./__generated__/ToolbarButtonAssignees_employees.graphql";
import { ToolbarButtonAssignees_roles$key } from "./__generated__/ToolbarButtonAssignees_roles.graphql";

import { ListItemRoles } from "../../TodoListItem/TodoListItemFooter/ListItemRoles";
import {
  OverlappingEmployeeAvatars,
  prioritizeEmployees,
  isViewerEmployee,
} from "../../TodoListItem/TodoListItemFooter/OverlappingEmployeeAvatars";

export function ToolbarButtonAssignees(props: {
  assignedEmployees: ToolbarButtonAssignees_employees$key;
  assignedRoles: ToolbarButtonAssignees_roles$key;
  onPress: () => void;
  isLocked: boolean;
}) {
  const employees = useFragment(
    graphql`
      fragment ToolbarButtonAssignees_employees on Employee
      @relay(plural: true) {
        id
        name
        isLinkedToViewer
        workStatus
        ...Avatar_employee
      }
    `,
    props.assignedEmployees
  );

  const employeeRoles = useFragment(
    graphql`
      fragment ToolbarButtonAssignees_roles on EmployeeRole
      @relay(plural: true) {
        id
        ...ListItemRoles_roles
      }
    `,
    props.assignedRoles
  );

  if (employees.length === 0 && employeeRoles.length === 0) {
    if (props.isLocked) {
      return null;
    }
    return (
      <DialogButton
        mode="inactive"
        onPress={props.onPress}
        indicatorIcon="user"
      >
        <FormattedMessage
          defaultMessage="Assign"
          description="Todo Toolbar Button Assignees Title"
        />
      </DialogButton>
    );
  }

  const hasEmployees = employees.length > 0;
  const hasRoles = employeeRoles.length > 0;

  const maxEmployees = Math.min(employees.length, hasRoles ? 3 : 5);
  const maxRoles = Math.min(employeeRoles.length, hasEmployees ? 1 : 2);
  const totalRemaining =
    employees.length - maxEmployees + (employeeRoles.length - maxRoles);

  const sorted = prioritizeEmployees(employees, undefined);
  const mostRelevant = sorted[0];
  const highlightName = mostRelevant
    ? isViewerEmployee(mostRelevant, undefined)
    : false;

  return (
    <DialogButton
      mode={props.isLocked ? "locked-active" : "active"}
      onPress={props.onPress}
      indicatorComponent={
        hasEmployees ? (
          <OverlappingEmployeeAvatars
            employees={employees}
            maxAvatars={maxEmployees}
            highlightViewer={true}
          />
        ) : null
      }
      trailing={
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          {hasRoles ? (
            <ListItemRoles
              roles={employeeRoles}
              maxShowingRoles={maxRoles}
              textType="detail"
              highlightViewerRoles={true}
            />
          ) : null}
          {totalRemaining > 0 ? (
            <Box spaceLeft={hasRoles ? "small" : undefined}>
              <AHText type="info-small" color="text-washed">
                {`+${totalRemaining}`}
              </AHText>
            </Box>
          ) : null}
        </View>
      }
    >
      {hasEmployees ? (
        <AHText
          type="info-small"
          font={AHTextFonts.MEDIUM}
          color={highlightName ? "darkerText" : "text-secondary"}
          numberOfLines={1}
        >
          {formatName(mostRelevant?.name ?? "")}
        </AHText>
      ) : null}
    </DialogButton>
  );
}
